import { isNil } from 'lodash';
import { useCallback, useContext, useEffect } from 'react';

import {
    PaymentTransactionContext,
    SubscriptionTransactionContext,
    TransactionContext,
    TransactionContextProviderProps,
    TransactionType,
} from '@/context/transaction';
import { PaymentTransactionContextProvider } from '@/context/transaction/PaymentTransactionContext';
import { SubscriptionTransactionContextProvider } from '@/context/transaction/SubscriptionTransactionContext';
import { TransactionContextValue } from '@/context/transaction/types';

export const TransactionContextProvider = (props: TransactionContextProviderProps) => {
    if (props.type === 'payment') {
        return <PaymentTransactionContextProvider {...props} />;
    }
    return <SubscriptionTransactionContextProvider {...props} />;
};

type TransactionContextType<T extends TransactionType | undefined> = T extends 'payment'
    ? PaymentTransactionContext
    : T extends 'subscription'
      ? SubscriptionTransactionContext
      : TransactionContextValue;

/**
 * @param type If the component is specific to payment or subscription contexts, supply the required type to get a properly-typed context. Components
 *   that are common to both transaction types should omit the argument.
 * @returns Closest transaction context, or throws if not within a transaction context or it's not the expected transaction type.
 */
export const useTransactionContext = <T extends TransactionType | undefined = undefined>(
    type?: T
): TransactionContextType<T> => {
    const context = useContext(TransactionContext);
    if (!context || (type && context.type !== type)) {
        throw new Error(`${type} transaction context required`);
    }
    return context as TransactionContextType<T>;
};

export const useTransactionContextLineItemQuantity = (
    price_id?: string,
    defaultQuantity?: number
): [number, (quantity: number) => void] => {
    const context = useTransactionContext();
    const { lineItemQuantities = undefined, setLineItemQuantity = undefined } = context.isPending ? {} : context;
    const loading = !lineItemQuantities || !setLineItemQuantity || !price_id;

    useEffect(() => {
        if (loading) {
            return;
        }

        if (isNil(lineItemQuantities[price_id]) && !isNil(defaultQuantity)) {
            setLineItemQuantity(price_id, defaultQuantity);
        }
    }, [context.isPending, defaultQuantity, lineItemQuantities, loading, price_id, setLineItemQuantity]);

    const setQuantity = useCallback(
        (quantity: number) => setLineItemQuantity!(price_id!, quantity),
        [price_id, setLineItemQuantity]
    );

    if (loading) {
        return [
            defaultQuantity ?? 0,
            () => {
                console.warn('unable to set quantity on loading transaction');
            },
        ];
    }

    return [lineItemQuantities![price_id] ?? defaultQuantity ?? 0, setQuantity];
};
